import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { FilterSpec, DateRangeBinding } from "./tableTypes";
import { filterParams, isColumnFiltered } from "./tableTypes";
import { defaultRange } from "./DateRangePicker";

interface Props {
  /** The header button the menu hangs off. */
  anchor: HTMLElement;
  header: string;
  sortKey?: string;
  filter?: FilterSpec;
  sort?: string;
  dir?: "asc" | "desc";
  onSortChange?: (sortKey: string, dir: "asc" | "desc") => void;
  filters?: Record<string, string>;
  facets?: Record<string, string[]>;
  onFilterChange?: (param: string, value: string) => void;
  dateRange?: DateRangeBinding;
  onClose: () => void;
}

/**
 * The per-column header menu: sort direction, this column's filter control,
 * and a clear entry.
 *
 * Rendered through a portal with fixed positioning rather than absolutely
 * inside the <th>: the table lives in a `.table-scroll` container with
 * `overflow: auto` (which is what lets the body scroll under a sticky
 * header), and that container clips any absolutely positioned descendant.
 */
export function ColumnMenu({
  anchor, header, sortKey, filter, sort, dir, onSortChange,
  filters, facets, onFilterChange, dateRange, onClose,
}: Props) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const a = anchor.getBoundingClientRect();
    const width = menuRef.current?.offsetWidth ?? 220;
    // Flip to the anchor's right edge when a left-aligned menu would run off
    // screen -- the rightmost columns (Source, FINTRAC Ref) otherwise open
    // into nothing.
    const left = Math.max(8, Math.min(a.left, window.innerWidth - width - 8));
    setPos({ top: a.bottom + 4, left });
  }, [anchor]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        anchor.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || anchor.contains(t)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [anchor, onClose]);

  const chooseSort = (next: "asc" | "desc") => {
    onSortChange?.(sortKey!, next);
    onClose();
  };

  const clearThisFilter = () => {
    if (!filter) return;
    if (filter.kind === "dateRange") {
      // A date column always has *some* range, so "clear" means back to the
      // default window rather than to nothing -- empty dates are a 400.
      const d = defaultRange();
      dateRange?.onChange(d.from, d.to);
    } else {
      filterParams(filter).forEach((p) => onFilterChange?.(p, ""));
    }
    onClose();
  };

  const showSort = Boolean(sortKey && onSortChange);
  const showClear = isColumnFiltered(filter, filters, dateRange);

  return createPortal(
    <div
      ref={menuRef}
      className="col-menu"
      role="menu"
      aria-label={`${header} options`}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: "hidden" }}
    >
      {showSort && (
        <div className="col-menu-group">
          <button role="menuitem" onClick={() => chooseSort("asc")}>
            <span className="glyph">↑</span> Sort ascending
            {sort === sortKey && dir === "asc" && <span className="tick">✓</span>}
          </button>
          <button role="menuitem" onClick={() => chooseSort("desc")}>
            <span className="glyph">↓</span> Sort descending
            {sort === sortKey && dir === "desc" && <span className="tick">✓</span>}
          </button>
        </div>
      )}

      {filter && (
        <div className="col-menu-group col-menu-filter">
          <FilterControl
            spec={filter}
            header={header}
            filters={filters}
            facets={facets}
            onFilterChange={onFilterChange}
            dateRange={dateRange}
          />
        </div>
      )}

      {showClear && (
        <div className="col-menu-group">
          <button role="menuitem" className="clear" onClick={clearThisFilter}>
            <span className="glyph">✕</span> Clear this filter
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
}

function FilterControl({
  spec, header, filters, facets, onFilterChange, dateRange,
}: {
  spec: FilterSpec;
  header: string;
  filters?: Record<string, string>;
  facets?: Record<string, string[]>;
  onFilterChange?: (param: string, value: string) => void;
  dateRange?: DateRangeBinding;
}) {
  if (spec.kind === "dateRange") {
    if (!dateRange) return null;
    return (
      <>
        <label>
          From
          <input
            type="date"
            aria-label="From"
            value={dateRange.from}
            onChange={(e) => dateRange.onChange(e.target.value, dateRange.to)}
          />
        </label>
        <label>
          To
          <input
            type="date"
            aria-label="To"
            value={dateRange.to}
            onChange={(e) => dateRange.onChange(dateRange.from, e.target.value)}
          />
        </label>
      </>
    );
  }

  if (!onFilterChange) return null;

  if (spec.kind === "select") {
    return (
      <label>
        {header}
        <select
          aria-label={spec.param}
          value={filters?.[spec.param] ?? ""}
          onChange={(e) => onFilterChange(spec.param, e.target.value)}
        >
          <option value="">All</option>
          {(facets?.[spec.param] ?? []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </label>
    );
  }

  if (spec.kind === "text") {
    return (
      <label>
        {header} contains
        <input
          aria-label={spec.param}
          value={filters?.[spec.param] ?? ""}
          onChange={(e) => onFilterChange(spec.param, e.target.value)}
        />
      </label>
    );
  }

  return (
    <>
      <label>
        Min
        <input
          aria-label={spec.minParam}
          inputMode="decimal"
          value={filters?.[spec.minParam] ?? ""}
          onChange={(e) => onFilterChange(spec.minParam, e.target.value)}
        />
      </label>
      <label>
        Max
        <input
          aria-label={spec.maxParam}
          inputMode="decimal"
          value={filters?.[spec.maxParam] ?? ""}
          onChange={(e) => onFilterChange(spec.maxParam, e.target.value)}
        />
      </label>
    </>
  );
}
